import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Crown, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { getJsLocale } from '../utils/dateLocale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default placeholder images for events
const DEFAULT_EVENT_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/99dd2eeb-d215-422d-a8a9-2afc31b2865d/images/93da89d4ff59ef271848b9fcfef377570ad731c65242eb9b146b881214a2942c.png",
  "https://static.prod-images.emergentagent.com/jobs/99dd2eeb-d215-422d-a8a9-2afc31b2865d/images/d0e79ca00eb85e78f0c8c5bc8012a17624a76048c9af69efef6eec7d49b7b45d.png",
  "https://static.prod-images.emergentagent.com/jobs/99dd2eeb-d215-422d-a8a9-2afc31b2865d/images/d4526a267ab361235f5f84122f51359c00d8d94912125ccfd51c4028c2c94fe2.png",
  "https://static.prod-images.emergentagent.com/jobs/99dd2eeb-d215-422d-a8a9-2afc31b2865d/images/fc50db845e99bcbc2a4e905f28f30e641311143825a6ea515d2c43f77ad717af.png"
];

const formatDate = (dateString, lang) => {
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString(getJsLocale(lang), options);
};

const formatTime = (dateString, lang) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString(getJsLocale(lang), { hour: '2-digit', minute: '2-digit' });
};

const FeaturedEvents = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [allEvents, setAllEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const pointerDownPos = useRef(null);

  // Fetch all events + meetups with images
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Fetch premium events, regular events, and meetups
        const [premiumRes, regularRes, meetupsRes] = await Promise.all([
          axios.get(`${API_URL}/api/events/premium`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/events`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/meetups`).catch(() => ({ data: [] })),
        ]);
        
        const premium = premiumRes.data || [];
        const regular = regularRes.data || [];
        const meetups = (meetupsRes.data || []).map(m => ({
          id: m.id,
          title: m.title,
          description: m.description || '',
          event_date: m.start_time,
          club_name: m.organizer_name || '',
          image_url: m.image_url || null,
          _isMeetup: true,
        }));
        
        // Combine: premium first, then regular events, then meetups
        const combined = [...premium, ...regular, ...meetups];
        // Deduplicate by id
        const seen = new Set();
        const unique = combined.filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true; });
        
        // Add default images to items without images, sort by date
        const withImages = unique
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
          .map((event, idx) => ({
            ...event,
            image_url: event.image_url || DEFAULT_EVENT_IMAGES[idx % DEFAULT_EVENT_IMAGES.length],
            isPremium: premium.some(p => p.id === event.id)
          }));
        
        setAllEvents(withImages.slice(0, 10));
      } catch (error) {
        console.log('Failed to fetch events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Auto-rotate carousel every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying || allEvents.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % allEvents.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, allEvents.length]);

  // Navigation functions
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % allEvents.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10s
  }, [allEvents.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + allEvents.length) % allEvents.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, [allEvents.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner-mc w-8 h-8" />
      </div>
    );
  }

  // If no events, show a placeholder
  if (allEvents.length === 0) {
    return (
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/5 rounded-xl border border-white/10 p-8 text-center">
            <Calendar className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
            <h3 className="font-heading text-2xl text-cream mb-2">{t('events.noEvents')}</h3>
            <p className="text-cream-muted">{t('events.noUpcomingEventsNow')}</p>
          </div>
        </div>
      </section>
    );
  }

  const currentEvent = allEvents[currentIndex];

  return (
    <section className="py-8 sm:py-16 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Event Carousel - Image Only */}
        <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden group">
          {/* Background Image with smooth transition */}
          <div
            className="relative h-[280px] sm:h-[400px] md:h-[500px] cursor-pointer"
            onPointerDown={(e) => { pointerDownPos.current = { x: e.clientX, y: e.clientY }; }}
            onClick={(e) => {
              if (!currentEvent) return;
              // Only navigate if the click wasn't a drag/swipe (moved less than 10px)
              if (pointerDownPos.current) {
                const dx = Math.abs(e.clientX - pointerDownPos.current.x);
                const dy = Math.abs(e.clientY - pointerDownPos.current.y);
                if (dx > 10 || dy > 10) return;
              }
              navigate(`/events/${currentEvent.id}`);
            }}
            data-testid="featured-event-clickable"
          >
            {allEvents.map((event, idx) => (
              <div
                key={event.id || idx}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  idx === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${event.image_url})` }}
                />
                {/* Dark Overlay - lighter for better image visibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/90 via-zinc-900/40 to-transparent" />
              </div>
            ))}
            
            {/* Content on image - minimal */}
            <div className="relative h-full p-4 sm:p-8 md:p-12 flex flex-col justify-end z-10">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {currentEvent.isPremium && (
                  <span className="px-2 py-1 sm:px-3 sm:py-1.5 bg-yellow-500 text-zinc-900 text-xs sm:text-sm font-bold rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3 sm:w-4 sm:h-4" />
                    PREMIUM
                  </span>
                )}
                {currentEvent.club_region && (
                  <span className="px-2 py-1 sm:px-3 sm:py-1.5 bg-zinc-800/80 backdrop-blur text-cream text-xs sm:text-sm rounded-full">
                    {currentEvent.club_region}
                  </span>
                )}
              </div>

              {/* Title */}
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-cream mb-2 sm:mb-3 max-w-3xl">
                {currentEvent.title || 'MC Event'}
              </h2>
              
              {/* Subtitle */}
              {currentEvent.description && (
                <p className="text-cream-muted text-sm sm:text-lg mb-3 sm:mb-4 max-w-2xl line-clamp-2">
                  {currentEvent.description}
                </p>
              )}

              {/* Event Details */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                {(currentEvent.event_date || currentEvent.date) && (
                  <div className="flex items-center gap-2 text-cream text-sm sm:text-base">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                    <span className="font-medium">
                      {formatDate(currentEvent.event_date || currentEvent.date, language)}
                    </span>
                  </div>
                )}
                {currentEvent.club_name && (
                  <div className="flex items-center gap-2 text-cream text-sm sm:text-base">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                    <span>{currentEvent.club_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Arrows - visible on hover */}
            {allEvents.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-zinc-900/60 backdrop-blur-sm border border-white/10 flex items-center justify-center text-cream hover:bg-zinc-800/80 transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Previous event"
                >
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-zinc-900/60 backdrop-blur-sm border border-white/10 flex items-center justify-center text-cream hover:bg-zinc-800/80 transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Next event"
                >
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Controls Below Image */}
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Dots Indicator - Always visible */}
          {allEvents.length > 1 && (
            <div className="flex items-center gap-2 order-2 sm:order-1">
              {allEvents.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToSlide(idx)}
                  className={`h-2.5 rounded-full transition-all ${
                    idx === currentIndex 
                      ? 'bg-green-500 w-8' 
                      : 'bg-zinc-600 w-2.5 hover:bg-zinc-500'
                  }`}
                  aria-label={`Gå til event ${idx + 1}`}
                />
              ))}
              <span className="ml-2 text-sm text-zinc-500">
                {currentIndex + 1} / {allEvents.length}
              </span>
            </div>
          )}
          
          {/* Empty div for spacing when no dots */}
          {allEvents.length <= 1 && <div className="order-2 sm:order-1"></div>}

          {/* CTA Button - Below image */}
          <div className="order-1 sm:order-2">
            <Link to="/events">
              <button className="bg-emerald-400 text-zinc-900 font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-emerald-300 hover:text-zinc-900 transition-colors flex items-center justify-center gap-2 px-6 py-3">
                <Calendar className="w-5 h-5" />
                {t('events.allEvents')}
              </button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
};

export default FeaturedEvents;
